import { Form, Input, InputNumber, Select } from "antd";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";

interface FormFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  type?: "text" | "password" | "email" | "textarea";
  required?: boolean;
  disabled?: boolean;
  prefix?: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  placeholder,
  type = "text",
  required = false,
  disabled = false,
  prefix,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Form.Item
      label={label}
      validateStatus={errors[name] ? "error" : ""}
      help={errors[name]?.message as string}
      required={required}
    >
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            {type === "password" ? (
              <Input.Password
                {...field}
                placeholder={placeholder}
                disabled={disabled}
                prefix={prefix}
                status={errors[name] ? "error" : ""}
                value={field.value ?? ""}
              />
            ) : type === "textarea" ? (
              <Input.TextArea
                {...field}
                placeholder={placeholder}
                disabled={disabled}
                status={errors[name] ? "error" : ""}
                value={field.value ?? ""}
              />
            ) : (
              <Input
                {...field}
                placeholder={placeholder}
                disabled={disabled}
                prefix={prefix}
                status={errors[name] ? "error" : ""}
                value={field.value ?? ""}
              />
            )}
          </>
        )}
      />
    </Form.Item>
  );
};

interface SelectFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: { value: string | number; label: string }[];
  required?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  placeholder,
  options,
  required = false,
  disabled = false,
  allowClear = true,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Form.Item
      label={label}
      validateStatus={errors[name] ? "error" : ""}
      help={errors[name]?.message as string}
      required={required}
    >
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            placeholder={placeholder}
            options={options}
            disabled={disabled}
            allowClear={allowClear}
            status={errors[name] ? "error" : ""}
          />
        )}
      />
    </Form.Item>
  );
};

interface NumberFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  required?: boolean;
  disabled?: boolean;
  formatter?: (value: number | undefined) => string;
  parser?: (value: string | undefined) => number;
}

export const NumberField: React.FC<NumberFieldProps> = ({
  name,
  label,
  placeholder,
  min,
  max,
  step,
  required = false,
  disabled = false,
  formatter,
  parser,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Form.Item
      label={label}
      validateStatus={errors[name] ? "error" : ""}
      help={errors[name]?.message as string}
      required={required}
    >
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <InputNumber
            {...field}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            formatter={formatter}
            parser={parser}
            style={{ width: "100%" }}
            status={errors[name] ? "error" : ""}
          />
        )}
      />
    </Form.Item>
  );
};

interface SubmitButtonProps {
  children: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  htmlType?: "button" | "submit" | "reset";
  className?: string;
  style?: React.CSSProperties;
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  children,
  loading = false,
  disabled = false,
  htmlType = "submit",
  className,
  style,
}) => {
  const {
    formState: { isValid },
  } = useFormContext();

  return (
    <button
      type={htmlType}
      disabled={disabled || !isValid}
      className={className}
      style={{
        ...style,
        opacity: !isValid || disabled ? 0.6 : 1,
        cursor: !isValid || disabled ? "not-allowed" : "pointer",
      }}
    >
      {loading ? "Loading..." : children}
    </button>
  );
};
